import PolicyShell, { Section, SubHead, Bullets } from "@/components/PolicyShell";

export default function PrivacyPage() {
  return (
    <PolicyShell
      testId="privacy-page"
      overline="BOSCAGE Elegance"
      title="Privacy Policy"
      contact="Contact: hello@boscage.com  ·  www.boscage.com"
      lastUpdated="Last updated: June 2026  |  BOSCAGE Elegance"
    >
      <p>
        This privacy notice for BoscageElgence (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) describes how
        and why we might collect, store, use, and/or share (&ldquo;process&rdquo;) your information when you use our
        services (&ldquo;Services&rdquo;), such as when you:
      </p>
      <Bullets
        items={[
          "Visit our website at http://www.boscage.com, or any website of ours that links to this privacy notice",
          "Engage with us in other related ways, including any sales, marketing, or events",
        ]}
      />
      <p>
        Questions or concerns? Reading this privacy notice will help you understand your privacy rights and choices. If
        you do not agree with our policies and practices, please do not use our Services. If you still have any
        questions or concerns, please contact us at hello@boscage.com.
      </p>

      <Section title="Summary of Key Points">
        <p>
          <strong className="text-[#000000]">What personal information do we process?</strong> When you visit, use, or
          navigate our Services, we may process personal information depending on how you interact with us and the
          Services, the choices you make, and the products and features you use.
        </p>
        <p>
          <strong className="text-[#000000]">Do we process any sensitive personal information?</strong> We do not
          process sensitive personal information.
        </p>
        <p>
          <strong className="text-[#000000]">Do we collect any information from third parties?</strong> We may collect
          information from public databases, marketing partners, social media platforms, and other outside sources.
        </p>
        <p>
          <strong className="text-[#000000]">How do we process your information?</strong> We process your information
          to provide, improve, and administer our Services, communicate with you, for security and fraud prevention,
          and to comply with law.
        </p>
        <p>
          <strong className="text-[#000000]">How do we keep your information safe?</strong> We have organizational and
          technical processes and procedures in place to protect your personal information. However, no electronic
          transmission over the internet can be guaranteed to be 100% secure.
        </p>
        <p>
          <strong className="text-[#000000]">What are your rights?</strong> Depending on where you are located
          geographically, the applicable privacy law may mean you have certain rights regarding your personal
          information.
        </p>
        <p>
          <strong className="text-[#000000]">How do you exercise your rights?</strong> The easiest way to exercise your
          rights is by submitting a data subject access request, or by contacting us at hello@boscage.com.
        </p>
      </Section>

      <Section title="1. What information do we collect?">
        <SubHead>Personal information you disclose to us</SubHead>
        <p className="italic text-[#000000]/65">
          In Short: We collect personal information that you provide to us.
        </p>
        <p>
          We collect personal information that you voluntarily provide to us when you register on the Services, express
          an interest in obtaining information about us or our products and Services, when you participate in
          activities on the Services, or otherwise when you contact us.
        </p>
        <p>
          <strong className="text-[#000000]">Personal Information Provided by You.</strong> The personal information we
          collect may include the following:
        </p>
        <Bullets items={["Names", "Phone numbers", "Email addresses", "Usernames", "Passwords"]} />
        <p>
          <strong className="text-[#000000]">Sensitive Information.</strong> We do not process sensitive information.
        </p>
        <p>
          <strong className="text-[#000000]">Payment Data.</strong> We may collect data necessary to process your
          payment if you choose to make purchases, such as your payment instrument number and the security code
          associated with your payment instrument. All payment data is handled and stored by PayPal, Payoneer, Credit
          card and Debit card.
        </p>
        <p>
          <strong className="text-[#000000]">Social Media Login Data.</strong> We may provide you with the option to
          register with us using your existing social media account details, like your Facebook, X, or other social
          media account.
        </p>

        <SubHead>Information automatically collected</SubHead>
        <p className="italic text-[#000000]/65">
          In Short: Some information &mdash; such as your Internet Protocol (IP) address and/or browser and device
          characteristics &mdash; is collected automatically when you visit our Services.
        </p>
        <Bullets
          items={[
            "Log and Usage Data: service-related, diagnostic, usage, and performance information automatically collected when you access or use our Services.",
            "Device Data: information about your computer, phone, tablet, or other device you use to access the Services.",
            "Location Data: information about your device's location, which can be either precise or imprecise. You can opt out of allowing us to collect this information by disabling your Location setting on your device.",
          ]}
        />

        <SubHead>Google API</SubHead>
        <p>
          Our use of information received from Google APIs will adhere to Google API Services User Data Policy,
          including the Limited Use requirements.
        </p>

        <SubHead>Information collected from other sources</SubHead>
        <p className="italic text-[#000000]/65">
          In Short: We may collect limited data from public databases, marketing partners, social media platforms, and
          other outside sources.
        </p>
        <p>
          We may obtain information about you from other sources such as public databases, joint marketing partners,
          affiliate programs, data providers, and social media platforms, for purposes of targeted advertising and
          event promotion.
        </p>
      </Section>

      <Section title="2. How do we process your information?">
        <p className="italic text-[#000000]/65">
          In Short: We process your information to provide, improve, and administer our Services, communicate with
          you, for security and fraud prevention, and to comply with law.
        </p>
        <Bullets
          items={[
            "To facilitate account creation and authentication and otherwise manage user accounts.",
            "To provide and improve our Services.",
            "For security and fraud prevention purposes.",
            "To comply with applicable laws and regulations.",
          ]}
        />
      </Section>

      <Section title="3. What legal bases do we rely on to process your information?">
        <p className="italic text-[#000000]/65">
          In Short: We only process your personal information when we believe it is necessary and we have a valid
          legal reason to do so under applicable law.
        </p>
        <p>
          If you are located in Canada, this section applies to you. We may process your information if you have given
          us specific permission (express consent) to use your personal information for a specific purpose, or in
          situations where your permission can be inferred (implied consent). You can withdraw your consent at any
          time.
        </p>
      </Section>

      <Section title="4. When and with whom do we share your personal information?">
        <p className="italic text-[#000000]/65">
          In Short: We may share information in specific situations and with specific third parties.
        </p>
        <p>
          <strong className="text-[#000000]">Business Transfers.</strong> We may share or transfer your information in
          connection with any merger, sale of company assets, financing, or acquisition of all or a portion of our
          business.
        </p>
        <p>
          <strong className="text-[#000000]">Google Maps Platform APIs.</strong> We may share your information with
          certain Google Maps Platform APIs. We obtain and store on your device your location for three (3) months. You
          may revoke your consent anytime by contacting us.
        </p>
      </Section>

      <Section title="5. Do we use cookies and other tracking technologies?">
        <p className="italic text-[#000000]/65">
          In Short: We may use cookies and other tracking technologies to collect and store your information.
        </p>
        <p>
          We may use cookies and similar tracking technologies to gather information when you interact with our
          Services. Some tracking technologies help us maintain the security of our Services, prevent crashes, fix
          bugs, save your preferences, and assist with basic site functions.
        </p>
        <p>
          We also permit third parties and service providers to use online tracking technologies on our Services for
          analytics and advertising purposes.
        </p>
        <p>
          <strong className="text-[#000000]">Google Analytics:</strong> We may share your information with Google
          Analytics to track and analyze the use of the Services. To opt out, visit
          https://tools.google.com/dlpage/gaoptout.
        </p>
      </Section>

      <Section title="6. How do we handle your social logins?">
        <p className="italic text-[#000000]/65">
          In Short: If you choose to register or log in to our Services using a social media account, we may have
          access to certain information about you.
        </p>
        <p>
          Our Services offer you the ability to register and log in using your third-party social media account details
          (like your Facebook or X logins). The profile information we receive may include your name, email address,
          friends list, and profile picture.
        </p>
      </Section>

      <Section title="7. Is your information transferred internationally?">
        <p className="italic text-[#000000]/65">
          In Short: We may transfer, store, and process your information in countries other than your own.
        </p>
        <p>
          Our servers are located in the United States. If you are accessing our Services from outside the US, please
          be aware that your information may be transferred to, stored, and processed in the US and other countries.
          We will take all necessary measures to protect your personal information in accordance with this privacy
          notice and applicable law.
        </p>
      </Section>

      <Section title="8. How long do we keep your information?">
        <p className="italic text-[#000000]/65">
          In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this privacy
          notice unless otherwise required by law.
        </p>
        <p>
          We will only keep your personal information for as long as it is necessary for the purposes set out in this
          privacy notice. No purpose in this notice will require us keeping your personal information for longer than
          the period of time in which users have an account with us.
        </p>
      </Section>

      <Section title="9. How do we keep your information safe?">
        <p className="italic text-[#000000]/65">
          In Short: We aim to protect your personal information through a system of organizational and technical
          security measures.
        </p>
        <p>
          We have implemented appropriate and reasonable technical and organizational security measures designed to
          protect the security of any personal information we process. However, no electronic transmission over the
          Internet can be guaranteed to be 100% secure. You should only access the Services within a secure
          environment.
        </p>
      </Section>

      <Section title="10. Do we collect information from minors?">
        <p className="italic text-[#000000]/65">
          In Short: We do not knowingly collect data from or market to children under 18 years of age.
        </p>
        <p>
          We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor do we
          knowingly sell such personal information. By using the Services, you represent that you are at least 18 or
          that you are the parent or guardian of such a minor and consent to such minor dependent&apos;s use of the
          Services. If you become aware of any data we may have collected from children under age 18, please contact
          us at hello@boscage.com.
        </p>
      </Section>

      <Section title="11. What are your privacy rights?">
        <p className="italic text-[#000000]/65">
          In Short: Depending on your state of residence in the US, you have rights that allow you greater access to
          and control over your personal information.
        </p>
        <p>
          You may review, change, or terminate your account at any time. You have certain rights under applicable data
          protection laws, including the right to: (i) request access and obtain a copy of your personal information;
          (ii) request rectification or erasure; (iii) restrict processing; (iv) data portability where applicable;
          and (v) not be subject to automated decision-making.
        </p>
        <p>
          <strong className="text-[#000000]">Withdrawing your consent:</strong> If we are relying on your consent to
          process your personal information, you have the right to withdraw your consent at any time by contacting us
          at hello@boscage.com.
        </p>
        <p>
          <strong className="text-[#000000]">Opting out of marketing:</strong> You can unsubscribe from our marketing
          communications at any time by clicking the unsubscribe link in the emails we send, or by contacting us.
        </p>
      </Section>

      <Section title="12. Controls for Do-Not-Track features">
        <p>
          Most web browsers and some mobile operating systems include a Do-Not-Track (&ldquo;DNT&rdquo;) feature or
          setting you can activate to signal your privacy preference. At this stage, no uniform technology standard
          for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT
          browser signals. California law requires us to let you know how we respond to web browser DNT signals
          &mdash; we do not respond to them at this time.
        </p>
      </Section>

      <Section title="13. Do United States residents have specific privacy rights?">
        <p className="italic text-[#000000]/65">
          In Short: If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa,
          Kentucky, Montana, New Hampshire, New Jersey, Oregon, Tennessee, Texas, Utah, or Virginia, you may have
          specific privacy rights.
        </p>
        <p>
          These rights include the right to request access to and receive details about the personal information we
          maintain about you, correct inaccuracies, get a copy of, or delete your personal information. These rights
          may be limited in some circumstances by applicable law.
        </p>

        <SubHead>Categories of Personal Information We Collect</SubHead>
        <p>We have collected the following categories of personal information in the past twelve (12) months:</p>
        <Bullets
          items={[
            "A. Identifiers (contact details, IP address, email address, account name) — YES",
            "B. Personal information as defined in the California Customer Records statute (name, contact information, financial information) — YES",
            "C. Protected classification characteristics (gender, age, demographic data) — YES",
            "D. Commercial information (transaction information, purchase history, payment information) — YES",
            "E. Biometric information — NO",
            "F. Internet or other similar network activity — NO",
            "G. Geolocation data — NO",
            "H. Audio, electronic, sensory, or similar information — NO",
            "I. Professional or employment-related information — NO",
            "J. Education Information — NO",
            "K. Inferences drawn from collected personal information — NO",
            "L. Sensitive personal information — NO",
          ]}
        />

        <SubHead>Your Rights</SubHead>
        <p>You have rights under certain US state data protection laws, including:</p>
        <Bullets
          items={[
            "Right to know whether or not we are processing your personal data",
            "Right to access your personal data",
            "Right to correct inaccuracies in your personal data",
            "Right to request the deletion of your personal data",
            "Right to obtain a copy of the personal data you previously shared with us",
            "Right to non-discrimination for exercising your rights",
            "Right to opt out of targeted advertising, the sale of personal data, or profiling",
          ]}
        />

        <SubHead>How to Exercise Your Rights</SubHead>
        <p>
          To exercise these rights, you can contact us by submitting a data subject access request, by emailing us at
          hello@boscage.com, or by visiting http://www.boscage.com/contact-us.
        </p>
        <p>
          We will honor your opt-out preferences if you enact the Global Privacy Control (GPC) opt-out signal on your
          browser.
        </p>

        <SubHead>Appeals</SubHead>
        <p>
          Under certain US state data protection laws, if we decline to take action regarding your request, you may
          appeal our decision by emailing us at hello@boscage.com. If your appeal is denied, you may submit a
          complaint to your state attorney general.
        </p>
      </Section>

      <Section title="14. Do we make updates to this notice?">
        <p className="italic text-[#000000]/65">
          In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.
        </p>
        <p>
          We may update this privacy notice from time to time. If we make material changes, we may notify you either
          by prominently posting a notice of such changes or by directly sending you a notification. We encourage you
          to review this privacy notice frequently.
        </p>
      </Section>

      <Section title="15. How can you contact us about this notice?">
        <p>
          If you have questions or comments about this notice, you may email us at hello@boscage.com or contact us by
          post at:
        </p>
        <address className="not-italic text-[#000000]/85 leading-relaxed">
          BoscageElgence
          <br />
          5856 Mount Vernon Dr, VA
          <br />
          Alexandria, VA 22303
          <br />
          United States
        </address>
      </Section>

      <Section title="16. How can you review, update, or delete the data we collect from you?">
        <p>
          Based on the applicable laws of your country or state of residence in the US, you may have the right to
          request access to the personal information we collect from you, details about how we have processed it,
          correct inaccuracies, or delete your personal information. To request to review, update, or delete your
          personal information, please submit a data subject access request or contact us at hello@boscage.com.
        </p>
      </Section>
    </PolicyShell>
  );
}
